import { useState } from "react";
import {
  User as UserIcon,
  Phone,
  MessageCircle,
  Pencil,
  Save,
  XCircle,
  Trash2,
  Star,
  Mail,
} from "lucide-react";
import api from "../../lib/api";
import { phoneToWa } from "../../lib/contact";
import ContactActions from "../ContactActions";
import FoldBlock from "./FoldBlock";
import Modal from "../Modal";
import ConfirmModal from "../ConfirmModal";
import BlurTextarea from "../BlurTextarea";

const ROLE_OPTIONS = [
  "Owner",
  "Manager",
  "Accounts",
  "Reception",
  "Maintenance",
  "Operations",
  "Procurement",
  "Technical",
  "Site supervisor",
  "Other",
];

const empty = {
  name: "",
  role: "",
  phone: "",
  whatsapp: "",
  email: "",
  is_primary: false,
  notes: "",
};

export default function ContactsBlock({ customerId, contacts, canEdit, onChanged, defaultOpen = true }) {
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null); // contact obj
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [otherRole, setOtherRole] = useState("");

  const reset = () => {
    setAddOpen(false);
    setForm(empty);
    setOtherRole("");
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const role = form.role === "Other" ? otherRole : form.role;
      await api.post("/contacts", { ...form, role, customer_id: customerId });
      reset();
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await api.put(`/contacts/${editing.id}`, { ...editing, customer_id: customerId });
      setEditing(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/contacts/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <FoldBlock
      icon={UserIcon}
      iconColor="#a78bfa"
      title="Contacts"
      count={contacts.length}
      defaultOpen={defaultOpen}
      canEdit={canEdit}
      onAdd={() => setAddOpen(true)}
      addLabel="Add contact"
      testId="block-contacts"
      emptyHint="No contacts. Add the owner / manager / reception."
    >
      <div className="grid md:grid-cols-2 gap-2">
        {contacts.map((c) => {
          const wa = (c.whatsapp || "").replace(/[^0-9]/g, "");
          const tel = (c.phone || "").replace(/[^0-9+]/g, "");
          return (
            <div
              key={c.id}
              className="border border-white/5 rounded-md p-3 bg-white/[0.02] hover:border-[#a78bfa]/30 transition-colors"
              data-testid={`contact-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <UserIcon className="w-4 h-4 text-[#a78bfa]" />
                    <div className="font-bold truncate">{c.name}</div>
                    {c.is_primary && <Star className="w-3 h-3 text-amber-300 fill-amber-300 shrink-0" />}
                  </div>
                  {(c.role || c.designation) && (
                    <div className="text-[10px] uppercase tracking-wider mt-1 text-[#a78bfa] inline-block px-1.5 py-0.5 rounded border border-[#a78bfa]/30 bg-[#a78bfa]/5">
                      {c.role || c.designation}
                    </div>
                  )}
                  {c.email && (
                    <div className="text-xs text-[#a0aabf] mt-1.5 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {c.email}
                    </div>
                  )}
                  {c.phone && <div className="text-xs text-[#a0aabf] mt-0.5">{c.phone}</div>}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <ContactActions
                    phone={tel || wa}
                    email={c.email}
                    name={c.name}
                    title={c.role || c.designation}
                    showVCard
                    compact
                    mobileOnlyExtras
                    testIdPrefix={`contact-${c.id}`}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setEditing({ ...c })}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1"
                      data-testid={`contact-edit-${c.id}`}
                      title="Edit"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setConfirmDel(c)}
                      className="hv-btn hv-btn-ghost text-xs px-2 py-1 text-rose-300"
                      data-testid={`contact-delete-${c.id}`}
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add modal */}
    </FoldBlock>
      <Modal open={addOpen} onClose={reset} title="Add contact" testId="contact-add-modal">
        <form onSubmit={add} className="space-y-3" data-testid="contact-form">
          <Field label="Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-input-name" />
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="hv-input mt-1"
              data-testid="contact-input-role"
            >
              <option value="">— Select role —</option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            {form.role === "Other" && (
              <input
                value={otherRole}
                onChange={(e) => setOtherRole(e.target.value)}
                placeholder="Specify role"
                className="hv-input mt-2"
                data-testid="contact-input-role-other"
              />
            )}
          </div>
          <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-input-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-input-phone" />
          <Field label="WhatsApp (intl. format)" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testid="contact-input-whatsapp" />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_primary}
              onChange={(e) => setForm({ ...form, is_primary: e.target.checked })}
            />
            Primary contact
          </label>
          <button type="submit" disabled={busy} className="hv-btn hv-btn-primary w-full" data-testid="contact-submit">
            {busy ? "Saving…" : "Save contact"}
          </button>
        </form>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit contact"
        testId="contact-edit-modal"
      >
        {editing && (
          <div className="space-y-3" data-testid="contact-edit-form">
            <Field label="Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="contact-edit-input-name" />
            <Field label="Role / designation" value={editing.role || editing.designation || ""} onChange={(v) => setEditing({ ...editing, role: v, designation: v })} testid="contact-edit-input-role" />
            <Field label="Email" value={editing.email} onChange={(v) => setEditing({ ...editing, email: v })} testid="contact-edit-input-email" />
            <Field label="Phone" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testid="contact-edit-input-phone" />
            <Field label="WhatsApp" value={editing.whatsapp} onChange={(v) => setEditing({ ...editing, whatsapp: v })} testid="contact-edit-input-whatsapp" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Notes</label>
              <BlurTextarea
                value={editing.notes}
                onSave={(v) => setEditing((s) => ({ ...s, notes: v }))}
                testid="contact-edit-input-notes"
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!editing.is_primary}
                onChange={(e) => setEditing({ ...editing, is_primary: e.target.checked })}
              />
              Primary contact
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
              <button type="button" onClick={() => setEditing(null)} className="hv-btn hv-btn-ghost">
                <XCircle className="w-4 h-4" /> Cancel
              </button>
              <button type="button" onClick={saveEdit} disabled={busy} className="hv-btn hv-btn-primary" data-testid="contact-edit-save">
                <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={del}
        title="Delete contact"
        message={
          <>
            Permanently delete contact <b>{confirmDel?.name}</b>?
          </>
        }
        confirmLabel="Delete contact"
        testId="contact-delete-confirm"
        busy={busy}
      />
    </>
  );
}

function Field({ label, value, onChange, testid, required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#a0aabf]">{label}</label>
      <input
        required={required}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="hv-input mt-1"
        data-testid={testid}
      />
    </div>
  );
}
